import throttle from 'lodash.throttle';
import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';

import VimeoPlayer from '@vimeo/player';


const state = window.teal_state = window.teal_state || {};
state.video = state.video || {};

const media_container_$ = '.media-figure';
const video_container_$ = '.media-figure__media';
const overlink_$ = '.video__overlink';
const controls_$ = '.video__control';
const html_player_$ = '.video__source';
const youtube_overlink_$ = '.youtube-overlink';
const youtube_standard_$ = '.youtube-standard';
const youtube_autoplay_$ = '.youtube-autoplay';
const vimeo_overlink_$ = '.vimeo-overlink';
const vimeo_standard_$ = '.vimeo-standard';
const vimeo_autoplay_$ = '.vimeo-autoplay';
const playback_button_$ = '.playback-toggle';
const video_autoplay_url_attr = 'data-url';
const image_$ = '.video__image';
const poster_visible_attr = 'data-poster-visible';
const play_status_attr = 'data-playing';
const iframe_$ = 'iframe';
const iframe_attr = 'data-iframe-id';

// Overshoot the exact container size to prevent rounding errors from causing
// the video to have visible borders at the edges
const size_overshoot = 1.02;
const aspect_ratio = 1.77777;

let has_video = false;
let $html_players = [];
let $youtube_overlinks = [];
let $youtube_standards = [];
let $youtube_autoplays = [];
let $vimeo_overlinks = [];
let $vimeo_standards = [];
let $vimeo_autoplays = [];
let $playback_buttons = [];
let previous_width = 0;
let previous_height = 0;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('video')) return false;
		
		state.video.init_youtube_players = () => {
			this.initYouTube();
		};
		
		load.content(() => {
			has_video = assign_elements();
			if (!has_video) return false;
			
			window.addEventListener(g.resize_e, throttle(() => {
				update_ambient_video_size();
			}, 150));
			update_ambient_video_size();
			
			if (state.video.init_youtube_players_ready) {
				state.video.init_youtube_players();
			}
		});
		load.all(() => {
			has_video = assign_elements();
			if (!has_video) return false;
			
			update_ambient_video_size();
			assign_change_events();
		});
	},
	
	
	/**
	 * Init YouTube iframe player API
	 * @link https://developers.google.com/youtube/iframe_api_reference
	 */
	initYouTube() {
		$youtube_autoplays.forEach($player => {
			setup_youtube_player($player);
		});
		update_ambient_video_size();
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_video) return true;
	
	// TODO: do not store objects on load, account for dynamic content
	
	// TODO: make entire frame clickable like overlink
	$html_players = document.querySelectorAll(html_player_$);
	
	$youtube_overlinks = document.querySelectorAll(youtube_overlink_$);
	$youtube_standards = document.querySelectorAll(youtube_standard_$);
	$youtube_autoplays = document.querySelectorAll(youtube_autoplay_$);
	
	$vimeo_overlinks = document.querySelectorAll(vimeo_overlink_$);
	$vimeo_standards = document.querySelectorAll(vimeo_standard_$);
	$vimeo_autoplays = document.querySelectorAll(vimeo_autoplay_$);
	
	$playback_buttons = document.querySelectorAll(playback_button_$);
	$playback_buttons.forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			toggle_video_playback($toggle);
		});
	});
	
	$html_players.forEach($player => {
		
		// TODO: apply this to other video types
		const reduced_motion = window.matchMedia('(prefers-reduced-motion: reduce)');
		const is_reduced = (
			reduced_motion === true
			|| reduced_motion.matches === true
		);
		if (is_reduced) {
			$player.removeAttribute('autoplay');
		}
		
		[g.play_e, g.pause_e].forEach(event_name => {
			$player.addEventListener(event_name, e => {
				update_play_status($player, event_name === g.play_e);
			});
		});
	});
	
	$youtube_overlinks.forEach($overlink => {
		$overlink.addEventListener(g.click_e, e => {
			load_youtube_by_click(e);
		});
	});
	
	$youtube_autoplays.forEach($autoplay => {
		const url = $autoplay.getAttribute(video_autoplay_url_attr);
		load_iframe_video(url, $autoplay);
	});
	
	$vimeo_overlinks.forEach($overlink => {
		$overlink.addEventListener(g.click_e, e => {
			load_vimeo_by_click(e);
		});
	});
	
	$vimeo_autoplays.forEach($autoplay => {
		const url = $autoplay.getAttribute(video_autoplay_url_attr);
		load_iframe_video(url, $autoplay, () => {
			setup_vimeo_player($autoplay);
		});
	});

	// Fetch thumbnails for Vimeo videos that don't have an explicit image.
	// Works for public videos; silently fails for unlisted/private.
	document.querySelectorAll('[data-vimeo-thumbnail]').forEach(fetch_vimeo_thumbnail);

	return true;
}


/**
 * Assign change events
 */
function assign_change_events() {
	document.addEventListener(layout.change_e, e => {
		update_ambient_video_size();
	});
}


/**
 * Toggle video playback for HTML videos
 * @param {element} $toggle
 */
function toggle_video_playback($toggle) {
	const video_id = $toggle.getAttribute('aria-controls');
	if (!video_id) return false;
	
	const $iframe_video = document.querySelector(`[${iframe_attr}="${video_id}"]`);
	if ($iframe_video) {
		const $overlink = $iframe_video.querySelector(overlink_$);
		if ($overlink) {
			$overlink.click();
			return false;
		}
	}
	
	const $video = document.querySelector(`#${video_id}`);
	if (!$video) return false;
	
	if (is_currently_playing($video)) {
		$video.pause();
	} else {
		$video.play();
	}
}


/**
 * Update video play status
 * @param {element} $video
 * @param {boolean} is_playing
 */
function update_play_status($video, is_playing) {
	const $toggle = document.querySelector(`[aria-controls="${$video.id}"]`);
	if (!$toggle) return false;
	
	const $container = $video.closest(media_container_$);
	
	if (is_playing) {
		$container.setAttribute(play_status_attr, g.true_s);
		
		// Remove image so it doesn't peek through at certain viewport sizes, but
		// only if the poster is not shown on pause
		const $image = $video
			.closest(media_container_$)
			.querySelector(`${image_$}:not([${poster_visible_attr}])`)
		;
		if ($image) {
			$image.remove();
		}
	} else {
		$container.setAttribute(play_status_attr, g.false_s);
	}
}


/**
 * Load YouTube video by click
 * @param {event} e
 */
function load_youtube_by_click(e) {
	e.preventDefault();
	e.stopPropagation();
	
	const $container = e.target.closest(video_container_$);
	const $link = e.target.closest(overlink_$);
	load_iframe_video($link.getAttribute('href'), $container, () => {
		$container.querySelector(controls_$).remove();
		$container.querySelector(overlink_$).remove();
		setup_youtube_player($container, () => {
			$container.querySelectorAll(':scope > :not(iframe)').forEach($el => {
				$el.remove();
			});
		});
	});
}


/**
 * Load Vimeo video by click
 * @param {event} e
 */
function load_vimeo_by_click(e) {
	e.preventDefault();
	e.stopPropagation();
	
	const $container = e.target.closest(video_container_$);
	const $link = e.target.closest(overlink_$);
	load_iframe_video($link.getAttribute('href'), $container, () => {
		$container.querySelector(controls_$).remove();
		$container.querySelector(overlink_$).remove();
		setup_vimeo_player($container, () => {
			$container.querySelectorAll(':scope > :not(iframe)').forEach($el => {
				$el.remove();
			});
		});
	});
}


/**
 * Load video in iframe
 * @param {string} url
 * @param {element} $container
 * @param {function} load_callback
 */
function load_iframe_video(url, $container, load_callback) {
	const $iframe = create_iframe(url, $container);
	$container.prepend($iframe);
	
	if (load_callback) load_callback();
}


/**
 * Create iframe
 * @param {string} url
 * @param {element} $container
 * @return {element}
 */
function create_iframe(url, $container) {
	const $iframe = document.createElement(iframe_$);
	const iframe_id = $container.getAttribute(iframe_attr);
	$iframe.src = clean_url(url);
	$iframe.setAttribute('id', `iframe-${iframe_id}`);
	$iframe.setAttribute('frameborder', 0);
	$iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; fullscreen; picture-in-picture');
	$iframe.setAttribute('allowfullscreen', true);
	return $iframe;
}


/**
 * Clean URL
 * @param {string} url
 * @return {string}
 */
function clean_url(url) {
	if (!url.includes('vimeo')) return url;
	
	return clean_vimeo_url(url);
}


/**
 * Make Vimeo URL compatible with embed
 * @param {string} url
 * @return {string}
 */
function clean_vimeo_url(url) {
	const data = get_vimeo_data(url);
	const id = data?.id;
	if (!id) return url;
	
	const hash = data?.hash ? `?h=${data.hash}` : '';
	const joiner = hash ? '&' : '?';
	
	return `https://player.vimeo.com/video/${id}${hash}${joiner}autoplay=1`;
}


/**
 * Extract Vimeo video data from URL
 * @param {string} url
 * @return {{id: string, hash: string|null}|null}
 */
function get_vimeo_data(url) {
	const pattern = /https?:\/\/(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?(?:\?(.*))?/;
	const parts = url.match(pattern);
	if (!parts) return null;
	
	const params = new URLSearchParams(parts[3] || '');
	
	return {
		id: parts[1],
		hash: params.get('h') || parts[2] || null,
	};
}


/**
 * Extract YouTube video ID from URL
 * @param {string} url
 * @return {string}
 */
function get_youtube_id(url) {
	const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/gi;
	const parts = url.match(regex);
	return (parts)
		? parts[1]
		: null;
}


/**
 * Update ambient video size to fit any aspect ratio
 */
function update_ambient_video_size() {
	$youtube_autoplays.forEach($autoplay => {
		// Scale the video much wider than its container, in order to crop out the
		// video title and info that otherwise can't be hidden
		const youtube_width_scale = 5;
		update_video_size($autoplay, youtube_width_scale);
	});
	$vimeo_autoplays.forEach($autoplay => {
		update_video_size($autoplay);
	});
}


/**
 * Update video size to fill container without letterboxing or pillarboxing
 * @param {element} $container
 * @param {float} width_scale
 */
function update_video_size($container, width_scale = 1) {
	const width = layout.getWidth($container);
	const height = layout.getHeight($container);
	if (!width || !height) return false;
	if (width === previous_width && height === previous_height) return false;
	
	previous_width = width;
	previous_height = height;
	
	// TODO: cache iframes
	const $iframe = $container.querySelector(iframe_$);
	
	// TODO: see video.flexible_ratio property
	// if ($container.classList.contains(':flex-ratio-true')) {
	// }
	
	if (width / height > aspect_ratio) {
		$iframe.style.width = `${100 * width_scale * size_overshoot}%`;
		// TODO: this height calculation is off, too large
		$iframe.style.height = `${Math.ceil((width * size_overshoot) * (1 / aspect_ratio))}px`;
	} else {
		// TODO: this width calculation needs to factor in the width scale
		$iframe.style.width = `${Math.ceil((height * size_overshoot) * aspect_ratio)}px`;
		$iframe.style.height = `${100 * size_overshoot}%`;
	}
}


/**
 * Is this video currently playing?
 * @param {element} $video
 * @return {boolean}
 */
function is_currently_playing($video) {
	return (
		!$video.paused
		&& !$video.ended
		&& $video.readyState > 2
		// && $video.currentTime > 0 // TODO: this is problematic
	);
}


/**
 * Setup YouTube player
 * @param {element} $player
 * @param {function} play_callback
 */
function setup_youtube_player($player, play_callback) {
	const $iframe = $player.querySelector(iframe_$);
	if (!$iframe) return false;
	
	let ran_play_callback = false;
	const player = new YT.Player($iframe.getAttribute('id'), {
		playerVars: {
			origin: window.location.origin,
		},
		events: {
			onReady: on_ready,
			onStateChange: on_state_change,
		}
	});
	
	// Toggle playback by remote button
	const $container = $player.closest(media_container_$);
	$container.querySelectorAll(playback_button_$).forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			const play_status = player.getPlayerState();
			if (play_status !== YT.PlayerState.PAUSED) {
				player.pauseVideo();
			} else {
				player.playVideo();
			}
		});
	});
	
	
	/**
	 * Handle player ready event
	 * @param {event} e
	 */
	function on_ready(e) {
		const play_status = player.getPlayerState();
		const $container = $player.closest(media_container_$);
		
		// Check if autoplay video is already playing and update controls
		if (play_status === YT.PlayerState.PLAYING) {
			update_controls($container, true);
			if (play_callback && !ran_play_callback) {
				play_callback();
				ran_play_callback = true;
			}
		}
		
		// Add fallback polling to catch any missed state changes
		let poll_count = 0;
		const max_polls = 10;
		const poll_interval = 300;
		const state_poll = setInterval(() => {
			poll_count++;
			const current_state = player.getPlayerState();
			
			if (
				current_state === YT.PlayerState.PLAYING
				&& $container.getAttribute(play_status_attr) !== g.true_s
			) {
				update_controls($container, true);
				if (play_callback && !ran_play_callback) {
					play_callback();
					ran_play_callback = true;
				}
			}
			
			if (
				poll_count >= max_polls
				|| $container.getAttribute(play_status_attr) === g.true_s
			) {
				clearInterval(state_poll);
			}
		}, poll_interval);
	}
	
	
	/**
	 * Receive status on player state change
	 * @param {event} e
	 */
	function on_state_change(e) {
		const play_status = e.data;
		update_controls($container, play_status !== YT.PlayerState.PAUSED);
		
		if (play_status === YT.PlayerState.PLAYING) {
			if (play_callback && !ran_play_callback) {
				play_callback();
				ran_play_callback = true;
			}
		}
	}
	
}


/**
 * Setup Vimeo player
 * @param {element} $player
 * @param {function} play_callback
 */
function setup_vimeo_player($player, play_callback) {
	const $iframe = $player.querySelector(iframe_$);
	if (!$iframe) return false;
	
	let ran_play_callback = false;
	const player = new VimeoPlayer($iframe);
	
	// Toggle playback by remote button
	const $container = $player.closest(media_container_$);
	$container.querySelectorAll(playback_button_$).forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			player.getPaused().then(paused => {
				if (!paused) {
					player.pause();
				} else {
					player.play();
				}
			}).catch(error => {
			});
		});
	});
	
	player.on('play', e => {
		update_controls($container, true);
		if (play_callback && !ran_play_callback) {
			play_callback();
			ran_play_callback = true;
		}
	});
	player.on('pause', e => {
		update_controls($container, false);
	});
	
}



/**
 * Fetch a Vimeo thumbnail via oEmbed JSONP and inject it into the video's image container.
 * Uses JSONP because Vimeo's oEmbed endpoint doesn't support CORS.
 * Works for public videos; silently fails for unlisted/private.
 * @param {element} $container - The .media-figure__media element with data-vimeo-thumbnail
 */
function fetch_vimeo_thumbnail($container) {
	const vimeo_url = $container.getAttribute('data-vimeo-thumbnail');
	if (!vimeo_url) return;

	const callback_name = `vimeo_thumb_${Date.now()}_${Math.random().toString(36).slice(2)}`;
	const oembed_url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeo_url)}&callback=${callback_name}`;

	window[callback_name] = (data) => {
		delete window[callback_name];

		if (!data?.thumbnail_url) return;

		// Vimeo returns thumbnails at a default size — request a larger one
		const thumbnail = data.thumbnail_url.replace(/_\d+x\d+/, '_1280x720');

		const $image_wrapper = $container.querySelector(image_$);
		if (!$image_wrapper) return;

		const $img = document.createElement('img');
		$img.src = thumbnail;
		$img.alt = data.title || '';
		$img.loading = 'lazy';
		$image_wrapper.appendChild($img);

		$container.removeAttribute('data-vimeo-thumbnail');
	};

	const $script = document.createElement('script');
	$script.src = oembed_url;
	$script.onerror = () => {
		delete window[callback_name];
		$script.remove();
	};
	document.head.appendChild($script);
	$script.addEventListener('load', () => $script.remove());
}


/**
 * Update player controls based on player state
 * @param {element} $container
 * @param {boolean} is_playing
 */
function update_controls($container, is_playing) {
	$container.setAttribute(play_status_attr, is_playing
		? g.true_s
		: g.false_s
	);
}
